import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const [navSolid, setNavSolid] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setNavSolid(window.scrollY > 60);
    };
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      {/* Fixed Nav */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-700 ${
          navSolid
            ? 'bg-black/96 backdrop-blur-md border-b border-white/[0.05]'
            : 'bg-transparent'
        }`}
        style={{ height: '64px' }}
      >
        <div className="w-full h-full px-7 md:px-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <span className="font-display font-black text-white tracking-[0.28em] md:tracking-[0.35em] text-[9.5px] md:text-xs uppercase whitespace-nowrap">
              Urban Blings
            </span>
          </a>
          <a
            href="#early-access"
            className="hidden md:block font-display text-[9px] font-medium tracking-[0.35em] uppercase text-white/30 hover:text-white/60 transition-colors duration-400 cursor-pointer whitespace-nowrap border border-white/[0.12] hover:border-white/30 px-5 py-2"
          >
            Enter Early Access
          </a>
          {/* Mobile CTA — refined link */}
          <a
            href="#early-access"
            className="md:hidden font-display text-[8px] font-medium tracking-[0.3em] uppercase text-white/50 hover:text-white/80 transition-colors duration-300 cursor-pointer whitespace-nowrap border border-white/[0.15] px-4 py-[7px]"
            style={{ borderRadius: '1px' }}
          >
            Early Access
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section
        id="section-hero"
        data-section="Hero"
        ref={heroRef}
        className="relative w-full overflow-hidden"
        style={{ height: '100svh', minHeight: '640px' }}
      >
        {/* Background image */}
        <div
          className="absolute inset-0 w-full h-full overflow-hidden"
          style={{
            transform: isMobile ? 'none' : `translateY(${scrollY * 0.25}px)`,
            willChange: isMobile ? 'auto' : 'transform',
          }}
        >
          <img
            src="https://static.readdy.ai/image/6a30e07b0f7694c83ca4df4a3a91c3d2/c5036d28066a65e7e3c401fbdc0fe907.png"
            alt="Urban Blings — Luxury Urban Jewelry"
            className="w-full h-full object-cover"
            style={{
              // Mobile: center the ring in upper-center of frame
              objectPosition: isMobile ? '62% 18%' : '60% center',
              transform: isMobile ? 'scale(1.22) translate(2%, -4%)' : 'scale(1.13) translate(-2%, -2%)',
              transformOrigin: 'center top',
            }}
          />

          {/* Desktop gradient — left fade */}
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              background:
                'linear-gradient(to right, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.78) 30%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.08) 75%, transparent 90%)',
            }}
          />

          {/* Mobile gradient — transparent top so ring shows, strong bottom for text */}
          <div
            className="absolute inset-0 block md:hidden"
            style={{
              background:
                'linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.10) 30%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.88) 78%, rgba(0,0,0,0.97) 100%)',
            }}
          />

          {/* Mobile — radial highlight ring focus: subtle contrast boost around product */}
          <div
            className="absolute inset-0 block md:hidden pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 55% 38% at 62% 26%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.22) 100%)',
            }}
          />

          {/* Top vignette for nav legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />

          {/* Grain / noise layer */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
              opacity: 0.028,
              mixBlendMode: 'overlay',
            }}
          />
        </div>

        {/* Hero Content — mobile: bottom-anchored; desktop: vertically centered */}
        <div className="relative z-10 w-full h-full flex items-end md:items-center">
          <div
            className="px-7 md:px-16 lg:px-24 w-full pb-16 md:pb-0"
            style={{ maxWidth: isMobile ? '100%' : '680px' }}
          >

            {/* Pre-label */}
            <p
              className="font-display text-[8px] md:text-[9px] tracking-[0.42em] md:tracking-[0.55em] uppercase text-white/45 mb-5 md:mb-10 animate-fade-in opacity-0-init"
              style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
            >
              &#8212;&nbsp;&nbsp;Pre-Launch 2026
            </p>

            {/* Main Headline */}
            <h1
              className="font-display font-black uppercase text-white animate-fade-in-up opacity-0-init"
              style={{
                lineHeight: '1.08',
                letterSpacing: '-0.02em',
                animationDelay: '0.5s',
                animationFillMode: 'forwards',
              }}
            >
              {/* Line 1 */}
              <span
                className="block text-white/65"
                style={{
                  fontSize: isMobile ? 'clamp(20px, 5.2vw, 32px)' : 'clamp(32px, 4.2vw, 56px)',
                  letterSpacing: '0.06em',
                  fontWeight: 600,
                  lineHeight: '1.3',
                }}
              >
                Luxury for
              </span>

              {/* Line 2 — primary */}
              <span
                className="block"
                style={{
                  fontSize: isMobile ? 'clamp(40px, 10.5vw, 62px)' : 'clamp(54px, 8vw, 104px)',
                  letterSpacing: '-0.03em',
                  lineHeight: '1.0',
                }}
              >
                The{' '}
                <span style={{ color: '#C4974A' }}>Urban</span>
              </span>

              {/* Line 3 — must not break */}
              <span
                className="block text-white whitespace-nowrap"
                style={{
                  fontSize: isMobile ? 'clamp(36px, 9.5vw, 56px)' : 'clamp(50px, 7.2vw, 94px)',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.05',
                }}
              >
                Swagger
              </span>
            </h1>

            {/* Divider rule */}
            <div
              className="animate-fade-in-up opacity-0-init mt-5 md:mt-8 mb-4 md:mb-7"
              style={{ animationDelay: '0.75s', animationFillMode: 'forwards' }}
            >
              <div className="w-8 md:w-10 h-px bg-white/25" />
            </div>

            {/* Subtext — primary */}
            <p
              className="font-display font-light text-white/70 tracking-[0.16em] md:tracking-[0.18em] uppercase animate-fade-in-up opacity-0-init"
              style={{
                fontSize: '11px',
                lineHeight: '1.7',
                animationDelay: '0.85s',
                animationFillMode: 'forwards',
              }}
            >
              Built for those who move different.
            </p>

            {/* Scarcity */}
            <p
              className="font-display text-white/38 uppercase tracking-[0.22em] md:tracking-[0.3em] mt-2 mb-7 md:mb-10 animate-fade-in-up opacity-0-init"
              style={{
                fontSize: '9px',
                lineHeight: '1.6',
                animationDelay: '1s',
                animationFillMode: 'forwards',
              }}
            >
              Limited first drop.&nbsp;&nbsp;No restocks.
            </p>

            {/* CTA Button */}
            <div
              className="animate-fade-in-up opacity-0-init"
              style={{ animationDelay: '1.2s', animationFillMode: 'forwards' }}
            >
              <a
                href="#early-access"
                className="inline-block font-display font-semibold text-[10px] uppercase tracking-[0.3em] cursor-pointer whitespace-nowrap transition-all duration-400 hover:opacity-80 active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #8C6E3C 0%, #C4974A 35%, #A87840 65%, #7A5C2E 100%)',
                  color: '#F5ECD8',
                  borderRadius: '2px',
                  padding: isMobile ? '15px 34px' : '13px 32px',
                  boxShadow: isMobile
                    ? '0 6px 28px rgba(180, 140, 60, 0.28), 0 2px 8px rgba(0,0,0,0.4)'
                    : 'none',
                  letterSpacing: '0.32em',
                }}
              >
                Enter Early Access
              </a>
            </div>
          </div>
        </div>

        {/* Bottom fade into page */}
        <div className="absolute bottom-0 left-0 w-full h-24 md:h-48 bg-gradient-to-t from-[#0A0A0A] to-transparent pointer-events-none" />

        {/* Scroll indicator — desktop only */}
        <div
          className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3 animate-fade-in opacity-0-init"
          style={{ animationDelay: '2.2s', animationFillMode: 'forwards' }}
        >
          <span className="text-[8px] font-display tracking-[0.5em] uppercase text-white/20">Scroll</span>
          <div className="w-px h-8 md:h-10 bg-gradient-to-b from-white/20 to-transparent" />
        </div>
      </section>
    </>
  );
}
